import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { EmcOptions, FormTypes, SubGeographicalRegionService } from 'shared-lib';
import { RegionService } from '../../../../services/region.service';
import { AreaService } from '../../../../services/area.service';
import { UserService } from '../../../../services/user.service';
import { SystemComponents } from '../../../../models/role.model';
import { Tariff } from '../../../../models/tariff.model';
import { UserType } from '../../../../models/customer.model';
import { DateType, DynamicPropertiesFilter, SelectableItem } from '../../../../models/dynamic-buildings.model';
import {
  CUSTOMER_USER_TYPES,
  CUSTOMER_USE_OF_PREMISES,
  CUSTOMER_VALIDATION_STATUSES,
  DynamicCustomerFilter,
} from '../../../../models/dynamic-customers.model';

@Component({
  selector: 'app-customer-filter-window',
  templateUrl: './customer-filter-window.component.html',
  styleUrls: ['../../../dynamic-buildings/building-filter-window/building-filter-window.component.scss'],
})
export class CustomerFilterWindowComponent implements OnInit {
  @Input() filter: DynamicCustomerFilter = new DynamicCustomerFilter();
  @Input() filterOptions: EmcOptions[] = [];
  @Input() tariffs: Tariff[] = [];
  @Input() sltOptions: { key: number; value: string }[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() addFilter = new EventEmitter<void>();

  openedFilters: string[] = [];

  dateTypes = [
    { label: 'Last Update Time', translation: 'COMMON.DATE_TYPE.LAST_UPDATE', value: DateType.LastUpdateTime },
    { label: 'Tagged Date', translation: 'COMMON.DATE_TYPE.TAGGED', value: DateType.TaggedDate },
    { label: 'Validated Date', translation: 'COMMON.DATE_TYPE.VALIDATED', value: DateType.ValidatedDate },
    { label: 'Review Date', translation: 'COMMON.DATE_TYPE.REVIEW_DATE', value: DateType.ReviewDate },
  ];
  userTypes = CUSTOMER_USER_TYPES;
  validationStatuses = CUSTOMER_VALIDATION_STATUSES;
  useOfPremisesOptions = CUSTOMER_USE_OF_PREMISES;
  reviewStatuses = [
    { translation: 'COMMON.REVIEW_STATUS_OPTIONS.REVIEWED', value: true },
    { translation: 'COMMON.REVIEW_STATUS_OPTIONS.NOT_REVIEWED', value: false },
  ];
  auditStatuses = [
    { translation: 'COMMON.ASSET_STATUS.AUDITED', value: true },
    { translation: 'COMMON.ASSET_STATUS.NON_AUDITED', value: false },
  ];
  tagOptions = [
    { translation: 'COMMON.TAG.EXISTING', value: false },
    { translation: 'COMMON.TAG.NEW_ENTRY', value: true },
  ];
  // Attribute field types that can be used as filters (same set as shared-lib dynamic-filter-window)
  private optionFieldTypes = ['Dropdown', 'SingleSelect', 'MultiSelect'];

  regions: any[] = [];
  allAreas: any[] = [];
  areas: any[] = [];
  geoRegions: any[] = [];
  allSubGeoRegions: any[] = [];
  subGeoRegions: any[] = [];
  allUsers: SelectableItem[] = [];

  constructor(
    private regionService: RegionService,
    private areaService: AreaService,
    private userService: UserService,
    private subGeoService: SubGeographicalRegionService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    if (this.hasStaticFilter('RegionDistrict')) this.getRegions();
    if (this.hasStaticFilter('GeoSubGeoRegion')) this.getGeoRegions();
    if (this.filter.filterParams.userType) this.loadUsers();
  }

  private hasStaticFilter(name: string): boolean {
    return this.filterOptions.some(x => !x.attributeDefinitionId && x.staticPropertyName === name);
  }

  fieldType(option: EmcOptions): string {
    const type = option.attributeDefinition?.fieldType;
    return typeof type === 'number' ? FormTypes[type] : (type ?? '');
  }

  isOptionFilter(option: EmcOptions): boolean {
    return this.optionFieldTypes.includes(this.fieldType(option));
  }

  private getRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (res: any) => {
        const body = res.body;
        if (body?.status === 200 || body?.status === 'OK') {
          this.regions = body.data ?? [];
          this.getAreas();
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  private getAreas() {
    this.areaService.getAllForSelect().subscribe({
      next: (res: any) => {
        const body = res.body;
        if (body?.status === 200 || body?.status === 'OK') {
          this.allAreas = body.data ?? [];
          this.updateAreas();
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  private getGeoRegions() {
    this.subGeoService.getAllForMappings().subscribe({
      next: (res: any) => {
        const body = res.body;
        if (body?.status === 200 || body?.status === 'OK') {
          const data = body.data ?? [];
          this.allSubGeoRegions = data.map((x: any) => ({
            id: x.id,
            name: x.name,
            geographicalRegionId: x.geographicalRegion?.id,
          }));
          const seen = new Set<number>();
          this.geoRegions = data
            .filter((x: any) => x.geographicalRegion?.id && !seen.has(x.geographicalRegion.id) && seen.add(x.geographicalRegion.id))
            .map((x: any) => ({ id: x.geographicalRegion.id, name: x.geographicalRegion.name }));
          this.updateSubGeoRegions();
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  toggleFilter(name: string) {
    const idx = this.openedFilters.indexOf(name);
    if (idx > -1) this.openedFilters.splice(idx, 1);
    else this.openedFilters.push(name);
  }

  onCancel() { this.close.emit(); }
  onWindowClick(event: MouseEvent) { event.stopPropagation(); }

  onAddFilter() {
    this.addFilter.emit();
    this.onCancel();
  }

  onDateTypeChange() {
    this.filter.filterParams.dateType = this.dateTypes.find(x => x.label === this.filter.filterParams.dateTypeString)?.value ?? DateType.LastUpdateTime;
  }

  // ---- Region / District ----
  get filteredRegions() {
    const s = this.filter.filterParams.regionSearchText?.toLowerCase() ?? '';
    return s ? this.regions.filter(r => r.name.toLowerCase().includes(s)) : this.regions;
  }

  selectRegion(region: any) {
    const fp = this.filter.filterParams;
    fp.selectedRegionModel = fp.selectedRegionModel?.id === region.id ? null : { id: region.id, name: region.name };
    fp.selectedRegions = fp.selectedRegionModel ? [region.id] : [];
    fp.selectedAreaModels = [];
    fp.selectedAreas = [];
    this.updateAreas();
  }

  updateAreas() {
    const s = this.filter.filterParams.areaSearchText?.toLowerCase() ?? '';
    const regionId = this.filter.filterParams.selectedRegionModel?.id;
    this.areas = this.allAreas.filter(a => (!regionId || a.regionId === regionId) && (!s || a.name.toLowerCase().includes(s)));
  }

  isAreaSelected(area: SelectableItem): boolean {
    return this.filter.filterParams.selectedAreaModels.some(a => a.id === area.id);
  }

  onAreaSelect(area: SelectableItem, event: Event) {
    const fp = this.filter.filterParams;
    if ((event.target as HTMLInputElement).checked) fp.selectedAreaModels.push(area);
    else fp.selectedAreaModels = fp.selectedAreaModels.filter(a => a.id !== area.id);
    fp.selectedAreas = fp.selectedAreaModels.map(a => a.id);
  }

  // ---- Geo / Sub-Geo ----
  get filteredGeoRegions() {
    const s = this.filter.filterParams.geoSearchText?.toLowerCase() ?? '';
    return s ? this.geoRegions.filter(g => g.name.toLowerCase().includes(s)) : this.geoRegions;
  }

  selectGeoRegion(geo: any) {
    const fp = this.filter.filterParams;
    fp.selectedGeoRegionModel = fp.selectedGeoRegionModel?.id === geo.id ? null : { id: geo.id, name: geo.name };
    fp.selectedGeographicalRegions = fp.selectedGeoRegionModel ? [geo.id] : [];
    fp.selectedSubGeoModels = [];
    fp.selectedSubGeographicalRegions = [];
    this.updateSubGeoRegions();
  }

  updateSubGeoRegions() {
    const s = this.filter.filterParams.subGeoSearchText?.toLowerCase() ?? '';
    const geoId = this.filter.filterParams.selectedGeoRegionModel?.id;
    this.subGeoRegions = this.allSubGeoRegions.filter(sg => (!geoId || sg.geographicalRegionId === geoId) && (!s || sg.name.toLowerCase().includes(s)));
  }

  isSubGeoSelected(item: SelectableItem): boolean {
    return this.filter.filterParams.selectedSubGeoModels.some(s => s.id === item.id);
  }

  onSubGeoSelect(item: SelectableItem, event: Event) {
    const fp = this.filter.filterParams;
    if ((event.target as HTMLInputElement).checked) fp.selectedSubGeoModels.push(item);
    else fp.selectedSubGeoModels = fp.selectedSubGeoModels.filter(s => s.id !== item.id);
    fp.selectedSubGeographicalRegions = fp.selectedSubGeoModels.map(s => s.id);
  }

  // ---- Users ----
  onSelectUserType(value: number) {
    const fp = this.filter.filterParams;
    fp.userType = value;
    fp.selectedUserModels = [];
    fp.selectedUsers = [];
    this.loadUsers();
  }

  private loadUsers() {
    const userType = this.filter.filterParams.userType;
    const type = userType === UserType.FirstCapturedUser || userType === UserType.LastCapturedUser
      ? SystemComponents.Mobile
      : SystemComponents.Admin;
    this.userService.getUsersForFilter(type).subscribe({
      next: (res: any) => { this.allUsers = res?.data ?? []; },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  get filteredUsers() {
    const s = this.filter.filterParams.searchUsersText?.toLowerCase() ?? '';
    return s ? this.allUsers.filter(u => u.name.toLowerCase().includes(s)) : this.allUsers;
  }

  isUserSelected(user: SelectableItem): boolean {
    return this.filter.filterParams.selectedUserModels.some(u => u.id === user.id);
  }

  onSelectUser(user: SelectableItem, event: Event) {
    const fp = this.filter.filterParams;
    if ((event.target as HTMLInputElement).checked) fp.selectedUserModels.push(user);
    else fp.selectedUserModels = fp.selectedUserModels.filter(u => u.id !== user.id);
    fp.selectedUsers = fp.selectedUserModels.map(u => u.id);
  }

  // ---- Validation Status ----
  isValidationStatusSelected(status: number): boolean {
    return this.filter.filterParams.selectedStatuses.includes(status);
  }

  onValidationStatusChange(status: number, event: Event) {
    const fp = this.filter.filterParams;
    if ((event.target as HTMLInputElement).checked) fp.selectedStatuses.push(status);
    else fp.selectedStatuses = fp.selectedStatuses.filter(s => s !== status);
  }

  // ---- Dynamic attribute filters ----
  private getOrCreateDynamicFilter(attrId: number): DynamicPropertiesFilter {
    let df = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);
    if (!df) {
      df = new DynamicPropertiesFilter();
      df.attributeDefinitionId = attrId;
      this.filter.dynamicFilters.push(df);
    }
    return df;
  }

  private removeEmptyDynamicFilter(df: DynamicPropertiesFilter) {
    if (df.selectedOptions.length === 0 && !df.from && !df.to) {
      this.filter.dynamicFilters = this.filter.dynamicFilters.filter(f => f !== df);
    }
  }

  isOptionSelected(attrId: number, optionId: number): boolean {
    return this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId)?.selectedOptions.includes(optionId) ?? false;
  }

  onOptionChange(attrId: number, optionId: number, event: Event) {
    const df = this.getOrCreateDynamicFilter(attrId);
    if ((event.target as HTMLInputElement).checked) {
      if (!df.selectedOptions.includes(optionId)) df.selectedOptions.push(optionId);
    } else {
      df.selectedOptions = df.selectedOptions.filter(id => id !== optionId);
    }
    this.removeEmptyDynamicFilter(df);
  }

  getDynamicDateValue(attrId: number, field: 'from' | 'to'): string | null {
    const v = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId)?.[field];
    return v ? new Date(v).toISOString().slice(0, 10) : null;
  }

  onDynamicDateChange(attrId: number, field: 'from' | 'to', event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const df = this.getOrCreateDynamicFilter(attrId);
    df[field] = value ? new Date(value) : null;
    this.removeEmptyDynamicFilter(df);
  }
}
